import { differenceInCalendarDays } from 'date-fns'

import type { FieldType, IQuoteIndex } from '@/interfaces'

export class QuoteIndex implements IQuoteIndex {
	// Index signature for IRecord compatibility
	[key: string]: FieldType
	// Identity fields
	id: number | string
	quoteId: number | string
	quoteNumber: number
	revision: string

	// Subjob/Job relationship
	subjobId: number
	subjobNumber: string
	jobId: number
	jobNumber: string

	// Customer information
	clientId: string
	clientName: string
	clientCreateDate?: Date | null
	newClientFlag: boolean

	// Show/Event information
	showName?: string | null
	showDate?: Date | null

	// Dates
	quoteDate: Date
	expiryDate: Date

	// State and status
	quoteState: number
	quoteStateAlias: string
	quoteStatus: 'Open' | 'Closed' | string
	isContracted: boolean

	// Subjob contracting state
	subjobContractingState?: string | null
	subjobContractingStateAlias?: string | null

	// Financial
	totalPrice: number
	totalCost: number | null

	// People
	salespersonId: number
	salespersonName: string | null
	salesTeamName?: string | null

	// Tracking
	createDate: Date
	updateDate: Date
	lastModified?: Date | null
	createBy: number | null
	updateBy: number | null
	createByName: string | null
	updateByName: string | null
	createByEmail: string | null
	updateByEmail: string | null
	createDateTime?: Date | null
	updateDateTime?: Date | null

	// Subjob dates
	kickDate?: Date | null
	shipDate?: Date | null

	// SCR status
	scrStatus?: 'Approved' | 'Pending' | 'Not Required' | string | null
	scrPending?: boolean

	// Work Order
	workOrderId?: number | null

	// Computed fields from backend
	hash: string
	displayName: string

	// Summary fields
	lineCount: number
	hasAlternatives: boolean
	hasTax: boolean

	constructor({
		// Identity fields
		id,
		quoteId,
		quoteNumber,
		revision,
		// Subjob/Job relationship
		subjobId,
		subjobNumber,
		jobId,
		jobNumber,
		// Customer information
		clientId,
		clientName,
		clientCreateDate,
		newClientFlag,
		// Show/Event information
		showName,
		showDate,
		// Dates
		quoteDate,
		expiryDate,
		// State and status
		quoteState,
		quoteStateAlias,
		quoteStatus,
		isContracted,
		// Subjob contracting state
		subjobContractingState,
		subjobContractingStateAlias,
		// Financial
		totalPrice,
		totalCost,
		// People
		salespersonId,
		salespersonName,
		salesTeamName,
		// Tracking
		createDate,
		updateDate,
		lastModified,
		createBy,
		updateBy,
		createByName,
		updateByName,
		createByEmail,
		updateByEmail,
		createDateTime,
		updateDateTime,
		// Subjob dates
		kickDate,
		shipDate,
		// SCR status
		scrStatus,
		scrPending,
		// Work Order
		workOrderId,
		// Computed fields from backend
		hash,
		displayName,
		// Summary fields
		lineCount,
		hasAlternatives,
		hasTax
	}: Partial<IQuoteIndex> = {}) {
		// Identity fields
		this.id = id ?? quoteId ?? 0
		this.quoteId = quoteId ?? 0
		this.quoteNumber = quoteNumber ?? 0
		this.revision = revision ?? ''

		// Subjob/Job relationship
		this.subjobId = subjobId ?? 0
		this.subjobNumber = subjobNumber ?? ''
		this.jobId = jobId ?? 0
		this.jobNumber = jobNumber ?? ''

		// Customer information
		this.clientId = clientId ?? ''
		this.clientName = clientName ?? ''
		this.clientCreateDate = this.parseDate(clientCreateDate) ?? null
		this.newClientFlag = newClientFlag ?? false

		// Show/Event information
		this.showName = showName ?? null
		this.showDate = this.parseDate(showDate) ?? null

		// Dates - parse strings to Date objects
		this.quoteDate = this.parseDate(quoteDate) ?? new Date()
		this.expiryDate = this.parseDate(expiryDate) ?? new Date()

		// State and status
		this.quoteState = quoteState ?? 0
		this.quoteStateAlias = quoteStateAlias ?? 'Draft'
		this.quoteStatus = quoteStatus ?? 'Open'
		this.isContracted = isContracted ?? false

		// Subjob contracting state
		this.subjobContractingState = subjobContractingState ?? null
		this.subjobContractingStateAlias = subjobContractingStateAlias ?? null

		// Financial
		this.totalPrice = totalPrice ?? 0
		this.totalCost = totalCost ?? null

		// People
		this.salespersonId = salespersonId ?? 0
		this.salespersonName = salespersonName ?? null
		this.salesTeamName = salesTeamName ?? null

		// Tracking - parse strings to Date objects
		this.createDate = this.parseDate(createDate) ?? new Date()
		this.updateDate = this.parseDate(updateDate) ?? new Date()
		this.lastModified = this.parseDate(lastModified) ?? null
		this.createBy = createBy ?? null
		this.updateBy = updateBy ?? null
		this.createByName = createByName ?? null
		this.updateByName = updateByName ?? null
		this.createByEmail = createByEmail ?? null
		this.updateByEmail = updateByEmail ?? null
		this.createDateTime = this.parseDate(createDateTime) ?? null
		this.updateDateTime = this.parseDate(updateDateTime) ?? null

		// Subjob dates
		this.kickDate = this.parseDate(kickDate) ?? null
		this.shipDate = this.parseDate(shipDate) ?? null

		// SCR status
		this.scrStatus = scrStatus ?? null
		this.scrPending = scrPending ?? false

		// Work Order
		this.workOrderId = workOrderId ?? null

		// Computed fields from backend
		this.hash = hash ?? this.computeHash()
		this.displayName = displayName ?? this.computeDisplayName()

		// Summary fields
		this.lineCount = Number(lineCount ?? 0)
		this.hasAlternatives = Boolean(hasAlternatives ?? false)
		this.hasTax = Boolean(hasTax ?? false)
	}

	/**
	 * Parse a date string or Date object to a Date object
	 */
	private parseDate(date: Date | string | null | undefined): Date | null {
		if (!date) return null
		if (date instanceof Date) return date

		const parsed = new Date(date)
		return isNaN(parsed.getTime()) ? null : parsed
	}

	/**
	 * Compute hash if not provided by backend
	 * This should match the backend computation for consistency
	 */
	private computeHash(): string {
		if (this.updateDate) {
			return `${this.quoteId}_${this.updateDate.toISOString()}`
		} else if (this.createDate) {
			return `${this.quoteId}_${this.createDate.toISOString()}`
		}
		return `${this.quoteId}_new`
	}

	/**
	 * Compute display name if not provided by backend
	 */
	private computeDisplayName(): string {
		return `Quote #${this.quoteNumber}` + (this.revision ? ` Rev ${this.revision}` : '')
	}

	/**
	 * Get a formatted date string for display
	 */
	getFormattedQuoteDate(): string {
		return this.quoteDate.toLocaleDateString()
	}

	/**
	 * Get a formatted date string for display
	 */
	getFormattedExpiryDate(): string {
		return this.expiryDate.toLocaleDateString()
	}

	/**
	 * Check if quote is expired
	 */
	isExpired(): boolean {
		return new Date() > this.expiryDate
	}

	/**
	 * Get status badge color for UI
	 */
	getStatusColor(): string {
		switch (this.quoteState) {
			case 1:
				return 'secondary' // Draft
			case 2:
				return 'warning' // Pending Estimation
			case 3:
				return 'info' // Pending Approval
			case 4:
				return 'success' // Approved
			case 5:
				return 'primary' // Sent
			case 6:
				return 'success' // Accepted
			case 7:
				return 'danger' // Rejected
			case 8:
				return 'dark' // Cancelled
			case 9:
				return 'warning' // Needs Revision
			default:
				return 'secondary'
		}
	}

	/**
	 * Get formatted price for display
	 */
	getFormattedPrice(): string {
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD'
		}).format(this.totalPrice)
	}

	/**
	 * Get margin percentage if cost is available
	 */
	getMarginPercent(): number | null {
		if (this.totalCost === null || this.totalPrice === 0) return null

		const margin = ((this.totalPrice - this.totalCost) / this.totalPrice) * 100
		return Math.round(margin * 100) / 100 // Round to 2 decimal places
	}

	/**
	 * Get staleDays - calculated field showing days since creation
	 */
	get staleDays(): number {
		const today = new Date()
		return differenceInCalendarDays(today, this.createDate)
	}

	/**
	 * Check if a work order has been generated from this quote
	 */
	get woGenerated(): boolean {
		return !!this.workOrderId && this.workOrderId > 0
	}
}
